import { BRAND_COLOR_CATEGORIES, type BrandColorCategory } from './types';
import type { BrandSource } from './fromBrand';

/**
 * Editing the palette.
 *
 * ── Why this is a whole-object rebuild ──
 *
 * `updateBrand` takes `colorPalette` as a complete `BrandColorPaletteInput`,
 * not a patch. Sending only the bucket you touched deletes the other three. So
 * every edit rebuilds all four, and that rebuild — four near-identical `.map()`
 * blocks stripping `__typename` and normalising `usage` — was copy-pasted twice
 * per app, four times in total, in the page this replaces.
 *
 * It is the kind of code that is wrong silently: drop a bucket and the colors
 * are gone with no error. Once, here, is enough.
 */

export interface PaletteColorInput {
	name: string;
	hex: string;
	usage?: string;
	/**
	 * `BrandPantoneRefInput` — book and printed code only.
	 *
	 * The queried ref also carries `color`, the resolved catalogue row. That is
	 * an OUTPUT field and rejects on write, so it is stripped here rather than
	 * at every call site. `hex` is optional and better left off: omit it and
	 * the backend fills in the book's own value, which is the one that is
	 * right by definition.
	 */
	pantone?: PaletteColorPantoneInput | null;
}

export interface PaletteColorPantoneInput {
	book: string;
	code: string;
	hex?: string | null;
}

/**
 * A ref as it goes back to the API.
 *
 * Writes are validated: an unknown book or code REJECTS THE WHOLE PALETTE, and
 * surviving refs are rewritten to the book's own casing and hex. So nothing
 * downstream may assume what it sent is what landed — re-read the brand after
 * saving rather than trusting the local copy.
 */
function toPantoneInput(
	pantone: PaletteColorPantoneInput | null | undefined
): PaletteColorPantoneInput | undefined {
	if (!pantone) return undefined;
	return { book: pantone.book, code: pantone.code, hex: pantone.hex ?? undefined };
}

export type PaletteInput = Record<BrandColorCategory, PaletteColorInput[]>;

/** Every bucket, stripped to what the input type accepts. */
export function toPaletteInput(source: Pick<BrandSource, 'colorPalette'>): PaletteInput {
	const palette = source.colorPalette;
	const out = {} as PaletteInput;
	for (const category of BRAND_COLOR_CATEGORIES) {
		out[category] = (palette?.[category] ?? []).map((color) => ({
			name: color.name ?? '',
			hex: color.hex,
			/* An empty string and "no usage" mean the same thing to the API and
			   different things to a differ — send neither. */
			usage: color.usage || undefined,
			/* Carried through, not dropped. Every edit rebuilds all four
			   buckets, so a colour whose pantone is not copied here loses its
			   print spec the next time ANY other colour is touched. */
			pantone: toPantoneInput(color.pantone),
		}));
	}
	return out;
}

export function paletteWithColorAdded(
	source: Pick<BrandSource, 'colorPalette'>,
	category: BrandColorCategory,
	color: PaletteColorInput
): PaletteInput {
	const palette = toPaletteInput(source);
	return {
		...palette,
		[category]: [
			...palette[category],
			{
				name: color.name,
				hex: color.hex,
				usage: color.usage || undefined,
				pantone: toPantoneInput(color.pantone),
			},
		],
	};
}

/** `index` is the position within its own category — the same one `BrandSwatch`
 * carries, and the only address a color has. */
export function paletteWithColorRemoved(
	source: Pick<BrandSource, 'colorPalette'>,
	category: BrandColorCategory,
	index: number
): PaletteInput {
	const palette = toPaletteInput(source);
	return {
		...palette,
		[category]: palette[category].filter((_, i) => i !== index),
	};
}

/**
 * Replace a color in place — or move it to another bucket.
 *
 * The category is part of what the modal edits, and a category change is not an
 * edit of the color's own record: the palette is four separate arrays, so
 * "primary → accent" means removing it from one and appending to the other.
 * Doing that as remove-then-add in the caller would go through `toPaletteInput`
 * twice and, on the same-category path, silently move the color to the end of
 * its own bucket. Handling both here keeps the order stable when the bucket
 * does not change, which is the common case.
 */
export function paletteWithColorUpdated(
	source: Pick<BrandSource, 'colorPalette'>,
	from: { category: BrandColorCategory; index: number },
	to: { category: BrandColorCategory; color: PaletteColorInput }
): PaletteInput {
	const palette = toPaletteInput(source);
	const next: PaletteColorInput = {
		name: to.color.name,
		hex: to.color.hex,
		usage: to.color.usage || undefined,
		pantone: toPantoneInput(to.color.pantone),
	};

	if (from.category === to.category) {
		return {
			...palette,
			[from.category]: palette[from.category].map((color, i) =>
				i === from.index ? next : color
			),
		};
	}

	return {
		...palette,
		[from.category]: palette[from.category].filter((_, i) => i !== from.index),
		[to.category]: [...palette[to.category], next],
	};
}
